import { Section } from '@/components/shared/Content';
import check from '@/static/assets/pet/check.svg';
import buttonConfiguration from '@/static/pet/button.json';
import doggyConfiguration from '@/static/pet/doggy.json';
import doggyLoopConfiguration from '@/static/pet/doggy_loop.json';
import { PRODUCT_PET } from '@/utils/products';
import { Box, Flex, font, useLottie } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import lottie, { type AnimationItem } from 'lottie-web';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { isAffiliateDiscountValid, PARTNER_NAMES, useAffiliateDiscount } from './hooks/useAffiliateDiscount';
import { SectionTitlePet } from './StyledElements';
import { MainCTA } from './TopSection';
import { useAllowedRegion } from './useAllowedRegion';

const Title = styled(SectionTitlePet)`
  white-space: pre-line;
  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: normal;
  `}
`;

const DoggySection = styled(Section)`
  position: relative;
`;

const DiscountsContainer = styled(Flex)<{ $itemCount?: number }>`
  width: ${({ $itemCount = 0 }) => ($itemCount <= 3 ? '626px' : '800px')};
  justify-content: space-between;

  ${MEDIA_QUERIES.mobile`
        width: 100%;
        flex-direction: column;
        align-items: center;
    `}
`;

const CADiscountsContainer = styled(DiscountsContainer)`
  width: 420px;
`;

const DoggyContainer = styled(Flex)`
  width: 100%;
  height: 294px;
  ${font('alt', { fontSize: '2.0rem' })}
  margin: -90px;
  z-index: 0;

  ${MEDIA_QUERIES.tabletAndDesktop`
        height: 487px;
        margin-top: 87px;
        position: relative;
        top: -85px;
        margin-bottom: -162px;

        path {
            stroke-width: 2 !important;
            vector-effect: non-scaling-stroke;
        }
    `}

  svg {
    margin-top: 12px;

    ${MEDIA_QUERIES.tabletAndDesktop`
            margin-top: 0;
        `}

    path {
      stroke-width: 1.5;
      vector-effect: non-scaling-stroke;
    }
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    svg g:nth-child(7) {
      /* dogs eyes in IE */
      mask: none;
    }
  }
`;

const StyledMainCTA = styled(MainCTA)`
  z-index: 1;
  margin-top: 17px;
  color: transparent;
  background: transparent !important;
  box-shadow: none;
  transform: rotate(${({ played }: { played: boolean }) => (played ? '8deg' : '0')})
    translate(1px, ${({ played }: { played: boolean }) => (played ? '-65px' : '-33px')});
  height: 59px;
  width: 240px;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-top: 17px;
        transform: rotate(${({ played }: { played: boolean }) => (played ? '8deg' : '0')})
        translate(1px, ${({ played }: { played: boolean }) => (played ? '30px' : '64px')});
    `};
`;

const ButtonLottie = styled(Box)`
  width: 625px;
  height: 250px;
  margin-top: -235px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        transform: translateY(90px);
    `}
`;

const StyledIcon = styled.img`
  width: 18px;
  height: 18px;
  margin-right: 7px;
`;

const Prefix = styled.span`
  ${font('main', { fontSize: '1.6rem', fontWeight: 700 })};

  ${MEDIA_QUERIES.tabletAndDesktop`
        ${font('main', { fontSize: '1.8rem', fontWeight: 700 })};
    `}
`;

const Text = styled.span`
  ${font('main', { fontSize: '1.6rem', fontWeight: 400 })}

  ${MEDIA_QUERIES.tabletAndDesktop`
        ${font('main', { fontSize: '1.8rem', fontWeight: 400 })};
    `}
`;

const CheckItem: React.FC<React.PropsWithChildren<{ prefix?: string }>> = ({ prefix, children }) => {
  return (
    <Flex alignItems="center" mb={[16, 0]}>
      <StyledIcon width="18" height="18" src={check.src} alt="checked" />
      {prefix && <Prefix>{prefix}</Prefix>}
      <Text>{children}</Text>
    </Flex>
  );
};

interface DoggyProps {
  title?: string;
  hidePercentage?: boolean;
}

export const Doggy: React.FC<DoggyProps> = ({ hidePercentage = false, title = 'Sweet prices.\nSweeter value' }) => {
  const { ref: mainCtaRef, inView } = useInView({
    threshold: 1,
    triggerOnce: true,
  });
  const doggyRef = useRef<Element | null>(null);
  const buttonRef = useRef(null);
  const doggyAnimation = useRef<null | AnimationItem>(null);
  const played = useRef(false);
  const isAllowedRegion = useAllowedRegion();
  const { affiliateDiscount } = useAffiliateDiscount();

  const { ref: sectionRef, inView: sectionInView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const { trackEvent } = useAnalyticsContext();

  useEffect(() => {
    if (sectionInView) {
      trackEvent('page.section.viewed', {
        section_name: 'doggy',
        page: 'pet',
        section_position: 9,
      });
    }
  }, [sectionInView]);

  const lottieButtonReference = useLottie(buttonRef, {
    animationData: buttonConfiguration,
    lottieConfig: {
      loop: false,
    },
  });

  useEffect(() => {
    if (!inView) return;

    if (lottieButtonReference) {
      lottieButtonReference.play();
    }

    if (!played.current) {
      doggyAnimation?.current?.play();
      played.current = true;
    }
  }, [lottieButtonReference, inView]);

  useEffect(() => {
    if (doggyAnimation.current !== null) return;
    if (doggyRef.current == null) return;

    doggyAnimation.current = lottie.loadAnimation({
      container: doggyRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData: doggyConfiguration,
    });

    doggyAnimation.current.addEventListener('complete', () => {
      if (doggyAnimation.current == null || doggyRef.current == null) return;
      doggyAnimation.current.destroy();

      doggyAnimation.current = lottie.loadAnimation({
        container: doggyRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: doggyLoopConfiguration,
      });

      doggyAnimation.current.play();
    });
  }, []);

  const mainCtaLabel = `${parse(title)}, Check our prices`;

  return (
    <DoggySection pt={[50, 100]} pb={[50, 0]} ref={sectionRef}>
      <Title>{parse(title)}</Title>

      {isAllowedRegion ? (
        <DiscountsContainer
          mt={[28, 35]}
          mb={[80, 30]}
          $itemCount={3 + (isAffiliateDiscountValid(affiliateDiscount) ? 1 : 0)}
        >
          <CheckItem prefix={hidePercentage ? undefined : '10%'}>
            {`${hidePercentage ? 'B' : '\u00A0b'}undle discount`}
          </CheckItem>
          <CheckItem prefix={hidePercentage ? undefined : '5%'}>
            {`${hidePercentage ? 'M' : '\u00A0m'}ulti-pet discount`}
          </CheckItem>
          <CheckItem prefix={hidePercentage ? undefined : '5%'}>
            {`${hidePercentage ? 'A' : '\u00A0a'}nnual discount`}
          </CheckItem>
          {affiliateDiscount && isAffiliateDiscountValid(affiliateDiscount) && (
            <CheckItem prefix={hidePercentage ? undefined : `${affiliateDiscount.discountPercent}%`}>
              {`${hidePercentage ? '' : '\u00A0'}${PARTNER_NAMES[affiliateDiscount.partnerCode]} discount`}
            </CheckItem>
          )}
        </DiscountsContainer>
      ) : (
        <CADiscountsContainer mt={[28, 35]} mb={[80, 30]}>
          <CheckItem prefix="10%">&nbsp;bundle discount</CheckItem>
          <CheckItem prefix="5%">&nbsp;multi-pet discount</CheckItem>
        </CADiscountsContainer>
      )}
      <StyledMainCTA
        played={played.current}
        flavor="primary"
        glowing
        data-source="pet"
        product={PRODUCT_PET}
        data-position="bottom"
        ref={mainCtaRef}
        aria-label={mainCtaLabel}
      >
        Check our prices
      </StyledMainCTA>
      <ButtonLottie ref={buttonRef} />
      <DoggyContainer ref={doggyRef} alignItems="center" justifyContent="center" />
    </DoggySection>
  );
};
